import {Control, ScaleLine, defaults as defaultControls} from 'ol/control';
import React, {useEffect, useState} from 'react';
import {fromLonLat, transform} from 'ol/proj';
import {Fill, Stroke, Style} from 'ol/style';
import VectorSource from 'ol/source/Vector';
import VectorLayer from 'ol/layer/Vector';
import {useNavigate} from 'react-router';
import {Polygon, Point} from 'ol/geom';
import TileLayer from 'ol/layer/Tile';
import Icon from 'ol/style/Icon';
import Feature from 'ol/Feature';
import Overlay from 'ol/Overlay';
import OSM from 'ol/source/OSM';
import View from 'ol/View';
import Map from 'ol/Map';
import 'ol/ol.css';
import {getCityName, getShortSpecName, getCountryName} from './Trans.js';
import {mapStyles, StyleTglBtn, MapObsPopup} from './MapStyles.js';
import {useGlobalStorage} from './mobx/GlobalStorage.js';
import {LIST_MAP_MAX_ELEM} from './ListMap.js';
import birderIcon from './images/watcher.png';
import {useWakeLock} from './UseWakeLock.js';
import birdIcon from './images/bird.png';
import {dStr, tStr} from './Util.js';
import {tt_grid} from './a_grid.js';
import ObsAppBar from './AppBar.js';
import {COMM_OK} from './WsR2.js';

function calcCrow(lat1, lon1, lat2, lon2) {
	var R = 6371; // km
	var dLat = toRad(lat2 - lat1);
	var dLon = toRad(lon2 - lon1);
	
	lat1 = toRad(lat1);
	lat2 = toRad(lat2);

	var a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
		Math.sin(dLon / 2) * Math.sin(dLon / 2) * Math.cos(lat1) * Math.cos(lat2); 
	var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a)); 
	var d = R * c;

	return d;
}

// Converts numeric degrees to radians
function toRad(Value) {
	return Value * Math.PI / 180;
}

function MapDestinationPopup(props) {
	function closeClick(e) {
		props.proceedDetails(1);
	}

	return (
		<div className="overlay-container" id="dest-popup" onClick={closeClick} style={{backgroundColor: 'white', padding: 8, border: '2px solid #cccccc', borderRadius: 10, boxShadow: '2px 4px #888888'}}>
			{/* <div className="overlay-closer" id="dest-closer" style={{textAlign: 'right', fontWeight: 'bold', paddingBottom: 8}}><a href='#'>x</a></div> */}
			{props.num > 1 ? <div className="overlay-text" id="obs-pos">{props.pos + 1 + ' / ' + props.num}</div> : null}
			<div className="overlay-text" id="dest-name" />
			<div className="overlay-text" id="dest-address" />
			<div className="overlay-text" id="dest-city" />
		</div>
	);
}

const MapBreedingCellToggleButton = (props) => {
	const leftPos = window.innerWidth - 40;

	const bg = props.breeding ? 'rgba(128, 128, 128, 0.8)' : 'rgba(255, 255, 255, 0)';

	return (
		<div>
			<button
			    className='ol-custom ol-control' 
			    id='breeding-tgl-btn'
			    style={{top: 40, left: leftPos, width: 32, height: 32, backgroundColor: bg}}
			    onClick={() => props.setBreeding(props.breeding ? false : true)}>
				B
			</button>
		</div>
	);
};

function NaviMap(props) {
	const [map, setMap] = useState(null);
	const [obsLayer, setObsLayer] = useState(null);
	const [clickFt, setClickFt] = useState({pos:0, fa:[]});
	const [obsStyle, setObsStyle] = useState(1);
	const gStorage = useGlobalStorage();

	const initLayers = () => {
		let center, zoom;

		if (props.pos && props.pos[0] && props.pos[1]) {
			center = fromLonLat(props.pos);
			zoom = 15;
		} else {
			/* Zoomaa Suomen kokoiseksi Jos koordinaatteja ei ole */
			center = fromLonLat([27.138596, 65.441896]);
			zoom = 12;
		}

		const initialObsLayer = new VectorLayer({source: new VectorSource()});

		let features = [];

		gStorage.lists.reglist.list.forEach((reg, ind) => {
			if (reg.public_loc || reg.user_id === gStorage.conn.user.id) {
// 				console.log('reg', reg.name, reg);
				const tgtFeat = new Feature({
					geometry: new Point(transform([reg.long_obs / 1000000.0, reg.lat_obs / 1000000.0], 'EPSG:4326', 'EPSG:3857')),
					reg: reg
				});

				const tgtStyle = new Style({
					image: new Icon(({
						anchor: [0.5, 1],
						src: birdIcon,
						size: [32, 32]
					}))
				});

				tgtFeat.setStyle(tgtStyle);
				features.push(tgtFeat);
			}
		});

		const newDestLayer = new VectorLayer({
			source: new VectorSource({
				features: features
			})
		});

		const initialMap = new Map({
			target: 'mapContainer',
			controls: defaultControls({rotate: false, zoom: false}).extend([new ScaleLine({units: 'metric'})]),

			layers: [
				new TileLayer({
					source: new OSM(),
				}),
				initialObsLayer,
				newDestLayer,
			],
			view: new View({
					center: center,
					zoom: zoom,
				}),
			overlays: [
				new Overlay({
					element: document.getElementById('dest-popup'),
					id: 'dest'
				}),
				new Overlay({
					element: document.getElementById('obs-popup'),
					id: 'obs'
				}),
			],
			moveTolerance: 8
		});

		initialMap.on('click', mapClick);
		initialMap.on('pointerdrag', props.startmove);

		initialMap.addControl(new Control({element: document.getElementById('style-tgl-btn')}));
		initialMap.addControl(new Control({element: document.getElementById('breeding-tgl-btn')}));

		setMap(initialMap);
		setObsLayer(initialObsLayer);
	}

	useEffect(() => {
		if (!obsLayer) {
			initLayers();
			return;
		}

		let features = [];

		let long_obs = props.pos[0];
		let lat_obs = props.pos[1];

		props.obs.forEach((obs, ind) => {
			let tgtFeat = new Feature({
				geometry: new Point(transform([obs.long_obs / 1000000.0, obs.lat_obs / 1000000.0], 'EPSG:4326', 'EPSG:3857')),
				obs: obs
			});

			tgtFeat.setStyle(mapStyles[obsStyle]);

			features.push(tgtFeat);
		});

		if (long_obs && lat_obs) {
			let obsFeat = new Feature({
				geometry: new Point(transform([long_obs, lat_obs], 'EPSG:4326', 'EPSG:3857'))
			});

			let obsStyle = new Style({
				image: new Icon(({
					anchor: [0.3, 1],
					src: birderIcon,
					size: [12, 32]
				}))
			});

			obsFeat.setStyle(obsStyle);
			features.push(obsFeat);

			if (props.followGps) {
				map.getView().setCenter(fromLonLat([long_obs, lat_obs]));
			}
		}

		if (props.cell) {
			const f = new Feature({
				geometry: new Polygon(tt_grid[props.cell.id]),
			});

			f.setStyle(
				new Style({
					stroke: new Stroke({
						color: 'grey',
						width: 2,
					}),
					fill: new Fill({
						color: 'rgba(128, 128, 128, 0.3)',
					}),
				})
			)

			features.push(f);
		}

		// set features to map
		obsLayer.setSource(
			new VectorSource({
				features: features,
			})
		)
		// eslint-disable-next-line
	}, [obsLayer, props.pos, props.obs, props.followGps, obsStyle, props.cell]);
	
	useEffect(() => {
// 		console.log('useEffect: clickFt', clickFt.pos, clickFt.fa.length);

		if (!map) {
// 			console.log('useEffect: clickFt null map!');
			return;
		}

		map.getOverlayById('dest').setPosition(undefined);
		map.getOverlayById('obs').setPosition(undefined);

		if (!clickFt.fa.length || clickFt.pos >= clickFt.fa.length)
			return;

		const reg = clickFt.fa[clickFt.pos].get('reg');
		if (reg) {
// 			console.log('reg', reg.name, reg.address, gStorage.lists.citylist.list.find((city) => city.id === reg.city_id).fi);

			map.getOverlayById('dest').setPosition(clickFt.fa[clickFt.pos].coordinate);

			document.getElementById('dest-name').innerHTML = reg.name;
			document.getElementById('dest-address').innerHTML = reg.address;
			document.getElementById('dest-city').innerHTML = props.getCityName(gStorage.lists.citylist.list.find((city) => city.id === reg.city_id));
		} else {
			const obs = clickFt.fa[clickFt.pos].get('obs');
			if (obs) {
				let d_str = '', t_str;

				d_str = dStr(obs.start_t);
				t_str = tStr(obs.start_t);
				if (t_str.length > 0 && t_str !== '0:00')
					d_str += ' ' + t_str;
				
				map.getOverlayById('obs').setPosition(clickFt.fa[clickFt.pos].coordinate);

				document.getElementById('obs-species').innerHTML = mapGetSpeciesName(gStorage.lists.speclist.list.find((taxon) => taxon.id === obs.spec_id)) + ', ' + obs.num;
				document.getElementById('obs-date').innerHTML = d_str;
				document.getElementById('obs-address').innerHTML = props.getCityName(gStorage.lists.citylist.list.find((city) => city.id === obs.city_id)) + ', ' + obs.addr;
			}
		}
		// eslint-disable-next-line
	}, [clickFt]);

	const mapClick = (e) => {
		let ft = [];

		e.map.forEachFeatureAtPixel(e.pixel, (feat) => {
			feat.coordinate = e.coordinate;
			ft.push(feat);
		});

		ft.sort((a, b) => {
			let f1, f2;

			f1 = a.get('reg');
			if (f1)
				return -1;
			f2 = b.get('reg');
			if (f2) 
				return 1;

			f1 = a.get('obs');
			f2 = b.get('obs');

			return f2.start_t - f1.start_t;
		});

		setClickFt({pos: 0, fa: ft});
	}

	function proceedDetails(dir) {
		setClickFt({pos: clickFt.pos + dir, fa: clickFt.fa});
	}

	function mapGetSpeciesName(spec) {
		return getShortSpecName(spec, gStorage.conn.user);
	}

	function toggleStyle() {
		let tmpStyle = obsStyle;

		tmpStyle++;
		if (tmpStyle >= mapStyles.length) 
			tmpStyle = 0;

		setObsStyle(tmpStyle);
	}

	return (<div id="mapContainer" style={{flexGrow: 1, height: window.innerHeight - document.getElementById('appbar').clientHeight}}>
			<MapDestinationPopup map={map} proceedDetails={proceedDetails} pos={clickFt.pos} num={clickFt.fa.length} />
			<MapObsPopup map={map} proceedDetails={proceedDetails} pos={clickFt.pos} num={clickFt.fa.length} />
			<StyleTglBtn tglStyle={toggleStyle} style={obsStyle}/>
			<MapBreedingCellToggleButton breeding={props.breeding} setBreeding={props.setBreeding} />
	       </div>);
}

function Navi() {
	const [showBreeding, setShowBreeding] = useState(false);
	const [followGps, setFollowGps] = useState(true);
	const [mapPos, setMapPos] = useState([0, 0]);
	const [obsPos, setObsPos] = useState([0, 0]);
	const [pos, setPos] = useState([0, 0]);
	const [cell, setCell] = useState(null);
	const [name, setName] = useState('-');
	const gStorage = useGlobalStorage();
	const [obs, setObs] = useState([]);
	const navigate = useNavigate();
	const { isSupported, released, request, release } = useWakeLock(
		() => console.log('Screen Wake Lock: requested!'),
		() => console.error('An error happened 💥'),
		() => console.log('Screen Wake Lock: released!')
	);

	const fromRefresh = document.getElementById('appbar') ? false : true;

// 	console.log('Navi', isSupported, released);
	useEffect(() => {
		if (fromRefresh)
			return;

		function handleVisibility() {
			if (document.visibilityState === 'visible' && isSupported && (released === undefined || released === true)) {
				request();
			}
		}

		document.addEventListener("visibilitychange", handleVisibility);
		if (isSupported && (released === undefined || released === true)) 
			request();

		const naviTmr = setInterval(naviTmrFn, 5000);
		naviTmrFn();

		return (() => {
// 			console.log('Navi: destructor');
			clearInterval(naviTmr);

			document.removeEventListener("visibilitychange", handleVisibility);
			release();
		});
		// eslint-disable-next-line
	}, []);

	const getheader = (() => {
		let  h = name;

		if (cell) {
			h += ' (' + cell.n + ':' + cell.e + ' ' + naviGetCityName(cell.city) + ', ' + cell.name + ')';
		}

		return h;
	});

	function naviGetCityName(city) {
		const usr = gStorage.conn.user;
		let a_str = '';

		if (city.country !== usr.def_country) {
			const country = gStorage.lists.countrylist.find(x => x.id === city.country);
			a_str = getCountryName(country, usr) + ', ';
		}

		return a_str += getCityName(city, gStorage.conn.user);
	}

	function naviRxWs(status, msg) {
		if (status !== COMM_OK) {
			console.error('naviRxWs: error ', status);
			return -1;
		}

		if (msg.cmd === 'geo') {
			if (msg.op === 'addr') {
				if (msg.result === 'OK') {
					if (msg.data.addr === 'not found') {
// 						console.log('naviRxWs: address for coordinates not found!');
						setName('-');
					} else {
						let addr = naviGetCityName(msg.data.city);

						if (msg.data.village) {
							if (msg.data.nearest && msg.data.nearest.id !== msg.data.village.id) {
								addr += ', ' + msg.data.village.fi + ', ' + msg.data.nearest.fi;
							} else {
								addr += ', ' + msg.data.village.fi;
							}
						}

						setName(addr);
					}
				} else {
					alert('naviRxWs:', msg.cmd, msg.op, 'failed!');
				}
			} else if (msg.op === 'cell') {
				if (msg.result === 'OK') {
					if (msg.data.cell === 'not found') {
// 						console.log('naviRxWs: cell for coordinates not found!');
						setCell(null);
					} else {
						msg.data.cell.city = gStorage.lists.citylist.list.find(c => c.id === msg.data.cell.city_id);
						setCell(msg.data.cell);
// 						setCell('(' + msg.data.cell.n + ':' + msg.data.cell.e + ' ' + naviGetCityName(c) + ', ' + msg.data.cell.name + ')');
					}
				} else {
					alert('naviRxWs:', msg.cmd, msg.op, 'failed!');
				}
			} else {
				console.error('naviRxWs: unknown operation:', msg.cmd, msg.op);
			}
		} else if (msg.cmd === 'obs') {
			if (msg.op === 'surrounding') {
// 				console.log('naviRxWs', 'received', msg.data.obssurrounding.length, 'obs!');
				setObs(msg.data.obssurrounding);
				setObsPos(pos);
			} else {
				console.error('naviRxWs: unknown operation:', msg.cmd, msg.op);
			}
		} else {
			console.error('naviRxWs: unknown cmd', msg.cmd, msg.op);
		}
	}

	function loadBreedingCell(lat, lon) {
// 		console.log('ask_cell', lat, lon);

		let msg = {
			sess_id: gStorage.conn.sess_id,
			cmd: 'geo',
			op: 'cell',
			data: {
				coord: {
					lat: Math.floor(lat * 1000000.0),
					long: Math.floor(lon * 1000000.0),
				},
				city_only_allowed: 0
			},
		};

		gStorage.conn.ws.send(msg, naviRxWs);
	}
	
	useEffect(() => {
		if (fromRefresh)
			return;

// 		console.log('posEffect', pos, mapPos, obsPos);
		if ((!pos[0] && !pos[1]) || (calcCrow(pos[1], pos[0], mapPos[1], mapPos[0]) >= 0.010)) {
			let msg = {
				sess_id: gStorage.conn.sess_id,
				cmd: 'geo',
				op: 'addr',
				data: {
					coord: {
						lat: Math.floor(pos[1] * 1000000.0),
						long: Math.floor(pos[0] * 1000000.0),
					},
					city_only_allowed: 1
				},
			};

			setMapPos(pos);

// 			console.log('posEffect: ask address');

			gStorage.conn.ws.send(msg, naviRxWs);

			if (showBreeding && pos[0] && pos[1]) {
				loadBreedingCell(pos[1], pos[0]);
			} else {
				setCell(null);
			}
		}

// 		console.log('posEffect', calcCrow(pos[1], pos[0], obsPos[1], obsPos[0]));

		if  ((!obsPos[0] && !obsPos[1]) || (calcCrow(pos[1], pos[0], obsPos[1], obsPos[0]) > 50 / 10)) {
			let msg = {
				sess_id: gStorage.conn.sess_id,
				cmd: 'obs',
				op: 'surrounding',
				data: {
					coord: {
						lat: Math.floor(pos[1] * 1000000.0),
						long: Math.floor(pos[0] * 1000000.0),
					},
					radius: 50000,
					limit: LIST_MAP_MAX_ELEM
				},
			};

// 			console.log('posEffect: ask obs', msg);
			gStorage.conn.ws.send(msg, naviRxWs);
		}
		// eslint-disable-next-line
	}, [pos]);

	useEffect(() => {
		if (showBreeding && pos[0] && pos[1]) {
			loadBreedingCell(pos[1], pos[0]);
		} else {
			setCell(null);
		}
		// eslint-disable-next-line
	}, [showBreeding]);

	function naviTmrFn() {
		if (!gStorage.conn.online)
			return;

		setPos([gStorage.gps.pos.longitude, gStorage.gps.pos.latitude]);
	}

	function naviStartMapMove(e) {
// 		console.log('naviStartMapMove');
		setFollowGps(false);
	}

	function centralizeMap() {
// 		console.log('centralizeMap');
		setFollowGps(true);
	}

	if (fromRefresh) {
		setTimeout(() => {
			navigate('/', { replace: true });
		}, 1);

		return null;
	}

	return (
		<div>
			<ObsAppBar getheader={getheader} followingGps={followGps} centralize_fn={centralizeMap}/>
			<NaviMap pos={mapPos} obs={obs} startmove={naviStartMapMove} followGps={followGps} breeding={showBreeding} setBreeding={setShowBreeding} cell={cell} getCityName={naviGetCityName}/>
		</div>
	);
}

export default Navi;
